/**
 * The single implementation of the admin re-authentication gate —
 * Document 6 §8.3, Architecture §6.6.
 *
 * Every admin-gated action routes through this component, so the ten-minute
 * window has one code path and one place to audit. A second copy of this
 * dialog somewhere else in the app would be a second chance to get it wrong.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { ApiError, api } from '../../api/client.js';
import { messageFor } from '../../api/error-messages.js';
import { useSession } from '../../session/SessionContext.js';
import { Button } from './Button.js';
import { Field } from './Field.js';
import './AdminReauthDialog.css';

interface AdminReauthDialogProps {
  /** Explains which action is being authorised. */
  action: string;
  onCancel: () => void;
  onConfirmed: () => void;
}

interface ReauthResponse {
  validForSeconds: number;
  validUntil: string | null;
}

export function AdminReauthDialog({
  action,
  onCancel,
  onConfirmed,
}: AdminReauthDialogProps) {
  const titleId = useId();
  const { markAdminReauthenticated } = useSession();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Doc 6 §19: focus starts on cancel, not on the action that proceeds.
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);

    try {
      const result = await api.post<ReauthResponse>('/api/auth/admin-reauth', {
        password,
      });
      markAdminReauthenticated(result.validUntil);
      setPassword('');
      onConfirmed();
    } catch (err) {
      setError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="reauth__backdrop" role="presentation">
      <div
        className="reauth__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h2 className="reauth__title" id={titleId}>
          Stüdyo şifresi gerekli
        </h2>
        <p className="reauth__body">{action}</p>

        <form onSubmit={submit} className="reauth__form">
          <Field
            label="Stüdyo şifresi"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={error}
            required
          />

          <div className="reauth__actions">
            <Button type="button" variant="secondary" onClick={onCancel} ref={cancelRef}>
              Vazgeç
            </Button>
            <Button type="submit" loading={busy}>
              {busy ? 'Doğrulanıyor…' : 'Onayla'}
            </Button>
          </div>
        </form>

        <p className="reauth__note">Onay sonrası bu yetki 10 dakika geçerlidir.</p>
      </div>
    </div>
  );
}
